import { CloudActions, CostCenter } from '@portal/portal-core';
import { flatten, sortBy, without } from 'lodash-es';
import { SuperAdminActions } from 'ducks/super-client';
import { SUSPENDED } from 'constants/filters';
import { parseDate } from 'utils';

// Date-like values accepted by parseDate (string | number | Date | null).
type DateLike = string | number | Date | null;

// Union of every admin cloud-action produced by the builders below. `ReturnType`
// distributes over the union of function types, yielding the union of their results.
type CloudAdminAction = ReturnType<
  | typeof CloudActions.adminSetProductSeats
  | typeof CloudActions.adminRemoveWhitelistDomain
  | typeof CloudActions.adminAddWhitelistDomain
  | typeof CloudActions.adminSetWhitelistIgnore
  | typeof CloudActions.adminSetAuthorizedDevicesOverride
  | typeof CloudActions.adminModifyProductAccessStart
  | typeof CloudActions.adminExtendProductAccess
  | typeof CloudActions.adminSetProductAccessActivityState
  | typeof CloudActions.adminSetProductAccessType
  | typeof CloudActions.adminUpdateCostCenterAndProductAccessOwner
  | typeof CloudActions.adminSetMaxSharedComputers
  | typeof CloudActions.adminSetCoadmins
  | typeof CloudActions.adminSetCountryCode
  | typeof CloudActions.adminUpdateCostCenterName
  | typeof CloudActions.adminAddFeatureToProductAccess
>;

// A single requested change from the manage-license UI. `before`/`after` shapes vary
// per `stateKey`, so they are narrowed (via casts) inside the individual builders.
interface Change {
  stateKey: string;
  before?: unknown;
  after?: unknown;
}

// Product feature entry carried in `enabledFeatures` change payloads.
interface ProductFeature {
  productFeatureId: string;
  name: string;
  value: unknown;
  enabled: boolean;
}

// Fields that the UI never changes but the actions still sometimes need.
interface LicenseOriginal {
  costCenterID: string;
  productAccessID: string;
  type: string;
  region: string;
  activityState: string;
}

// Minimal shape of the license object accessed by these builders.
interface License {
  productName: string;
  original: LicenseOriginal;
}

const buildSeatCountAction = (change: Change, license: License): CloudAdminAction => {
  const numSeats = Number(change.after);
  return CloudActions.adminSetProductSeats(
    license.original.costCenterID,
    license.original.productAccessID,
    numSeats,
  );
};

const buildWhitelistSuffixActions = (change: Change, license: License): CloudAdminAction[] => {
  const before = change.before as string[];
  const after = change.after as string[];
  const suffixesToRemove = without(before, ...after);
  const suffixesToAdd = without(after, ...before);

  const removeActions = suffixesToRemove.map((suffix) =>
    CloudActions.adminRemoveWhitelistDomain(license.original.costCenterID, suffix),
  );
  const addActions = suffixesToAdd.map((suffix) =>
    CloudActions.adminAddWhitelistDomain(license.original.costCenterID, suffix),
  );
  return [...removeActions, ...addActions];
};

const buildIgnoreWhitelistAction = (change: Change, license: License): CloudAdminAction =>
  CloudActions.adminSetWhitelistIgnore(license.original.costCenterID, change.after);

const buildAuthorizedDevicesOverride = (change: Change, license: License): CloudAdminAction =>
  CloudActions.adminSetAuthorizedDevicesOverride(
    license.original.costCenterID,
    license.original.productAccessID,
    change.after,
  );

const buildLicenseStartDateAction = (change: Change, license: License): CloudAdminAction =>
  CloudActions.adminModifyProductAccessStart(
    license.original.costCenterID,
    license.productName,
    license.original.type,
    license.original.region,
    parseDate(change.after as DateLike),
  );

// Important! This action relies on product access type (for some reason), which might ALSO change
// in a set of manage actions. We read `license.original.type` off the original (unedited) license
// state, so it's important that if both actions run together, this one run *first*, before the
// access type changes. So, changes are sorted (see `buildManageLicenseAction`) to ensure consistent
// iteration order across browsers.
const buildLicenseEndDateAction = (change: Change, license: License): CloudAdminAction =>
  CloudActions.adminExtendProductAccess(
    license.original.costCenterID,
    license.productName,
    license.original.type,
    license.original.region,
    parseDate(change.after as DateLike),
  );

const buildProductAccessTypeAction = (
  change: Change,
  license: License,
): CloudAdminAction | (CloudAdminAction | false)[] => {
  if (change.after === SUSPENDED.label) {
    return CloudActions.adminSetProductAccessActivityState(
      license.original.costCenterID,
      license.original.productAccessID,
      CostCenter.ACTIVITY_STATES.suspended,
    );
  }

  const wasPreviouslySuspended =
    license.original.activityState === CostCenter.ACTIVITY_STATES.suspended;
  return [
    CloudActions.adminSetProductAccessType(
      license.original.costCenterID,
      license.original.productAccessID,
      change.after,
    ),

    // Changing from suspended to anything else entails un-suspending:
    wasPreviouslySuspended &&
    CloudActions.adminSetProductAccessActivityState(
      license.original.costCenterID,
      license.original.productAccessID,
      CostCenter.ACTIVITY_STATES.active,
    ),
  ];
};

const buildProductAccessActivityStateAction = (
  change: Change,
  license: License,
): CloudAdminAction => {
  return CloudActions.adminSetProductAccessActivityState(
    license.original.costCenterID,
    license.original.productAccessID,
    change.after,
  );
};

const buildCostCenterOwnerChangeTypeAction = (change: Change, license: License): CloudAdminAction =>
  CloudActions.adminUpdateCostCenterAndProductAccessOwner(
    license.original.costCenterID,
    change.after,
  );

const buildAdminSetMaxSharedComputersTypeAction = (
  change: Change,
  license: License,
): CloudAdminAction => {
  const sharedComputerSeats = Number(change.after);
  return CloudActions.adminSetMaxSharedComputers(
    license.original.costCenterID,
    license.original.productAccessID,
    sharedComputerSeats,
  );
};

const buildAdminsAction = (change: Change, license: License): CloudAdminAction => {
  return CloudActions.adminSetCoadmins(license.original.costCenterID, change.after);
};

const buildCountryCode = (change: Change, license: License): CloudAdminAction =>
  CloudActions.adminSetCountryCode(license.original.costCenterID, change.after);

const buildCostCenterName = (change: Change, license: License): CloudAdminAction =>
  CloudActions.adminUpdateCostCenterName(license.original.costCenterID, change.after);

const buildEnabledFeaturesAction = (change: Change, license: License): CloudAdminAction[] => {
  // Build CloudActions only for features that were modified
  const before = (change.before || {}) as Record<string, ProductFeature>;
  const after = (change.after || {}) as Record<string, ProductFeature>;

  const afterValues = Object.values(after);

  // Only send actions for features that changed
  const actions = afterValues
    .filter((feature) => {
      // Check if this feature was modified
      const beforeFeature = Object.values(before).find(
        (f) => f.productFeatureId === feature.productFeatureId,
      );
      // Include if new, if enabled state changed, or if value changed
      return (
        !beforeFeature ||
        beforeFeature.enabled !== feature.enabled ||
        beforeFeature.value !== feature.value
      );
    })
    .map((feature) =>
      CloudActions.adminAddFeatureToProductAccess(
        feature.name,
        license.original.costCenterID,
        license.original.productAccessID,
        feature.value,
        feature.enabled,
      ),
    );

  return actions;
};

type BuildActionResult =
  | CloudAdminAction
  | (CloudAdminAction | false)[]
  | null
  | false;

const buildAction = (change: Change, license: License): BuildActionResult => {
  switch (change.stateKey) {
    case 'seatCount':
      return buildSeatCountAction(change, license);
    case 'whitelistSuffixes':
      return buildWhitelistSuffixActions(change, license);
    case 'licenseStartDate':
      return buildLicenseStartDateAction(change, license);
    case 'licenseEndDate':
      return buildLicenseEndDateAction(change, license);
    case 'activityState':
      return buildProductAccessActivityStateAction(change, license);
    case 'productAccessType':
      return buildProductAccessTypeAction(change, license);
    case 'email':
      return buildCostCenterOwnerChangeTypeAction(change, license);
    case 'maxSharedSeatCount':
      return buildAdminSetMaxSharedComputersTypeAction(change, license);
    case 'whitelistIgnore':
      return buildIgnoreWhitelistAction(change, license);
    case 'admins':
      return buildAdminsAction(change, license);
    case 'authorizedDevicesOverride':
      return buildAuthorizedDevicesOverride(change, license);
    case 'country':
      return buildCountryCode(change, license);
    case 'costCenterName':
      return buildCostCenterName(change, license);
    case 'enabledFeatures':
      return buildEnabledFeaturesAction(change, license);
    case 'firstName':
    case 'lastName':
      return null;
    default:
      console.warn(`"${change.stateKey}" switch case not found`);
      return false;
  }
};

// Takes an array of requested change objects with `stateKey` and `value` properties. Returns a
// superadmin "bulk changes action" with each change mapped to correct cloud action
export const buildManageLicenseAction = (
  changes: Change[],
  license: License,
): ReturnType<typeof SuperAdminActions.bulkActions> | false => {
  // Some changes generate multiple actions, hence `flatten`:
  const changeActions = flatten(
    sortBy(changes, 'stateKey') // See `buildLicenseEndDateAction`
      .map((change) => buildAction(change, license)),
  ).filter(Boolean);

  if (changeActions.length < 1) {
    return false;
  }

  return SuperAdminActions.bulkActions(changeActions);
};
